import React, { useCallback, useEffect, useState } from 'react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { Forms, FormsPart, FormsSub } from '.';
import { Events, scroller } from 'react-scroll';
import { v4 as uuidv4 } from 'uuid';
import PropTypes from 'prop-types';
import { Form, Spin } from 'antd';
import { Card } from './Card';
import MovePart from './MovePart';

import { QUESTION_TYPE } from './Question';
import MenuBar from './MenuBar';
import Skiptopart from './Skiptopart';
import { textOnly } from '../../util/util';

const reorder = (list, startIndex, endIndex) => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);

    return result;
};

/**
 * Moves an item from one list to another list.
 */
const move = (source, destination, droppableSource, droppableDestination) => {
    const sourceClone = Array.from(source);
    const destClone = Array.from(destination);
    const [removed] = sourceClone.splice(droppableSource.index, 1);

    destClone.splice(droppableDestination.index, 0, removed);

    const result = {};
    result[droppableSource.droppableId] = sourceClone;
    result[droppableDestination.droppableId] = destClone;

    return result;
};

const getItemStyle = (_, draggableStyle) => ({
    // some basic styles to make the items look a bit nicer
    userSelect: 'none',
    // styles we need to apply on draggables
    ...draggableStyle,
});

const isRadioORCheckbox = (type) =>
    type === QUESTION_TYPE.RADIO || type === QUESTION_TYPE.CHECKBOX;

export default function Move({ template }) {
    const form = Form.useFormInstance();
    const part = form.getFieldValue('part');

    const [selectItem, setSelectItem] = useState({
        id: 'main',
        type: 'main',
        group: '',
    });
    const [spinning, setSpinning] = useState(false);

    const [activeQuestionRef, setActiveQuestionRef] = useState(null);
    const [panelTopValue, setPanelTopValue] = useState(0);

    const [openModal, setOpenModal] = useState(false);

    const initItem = (answerType) => {
        const answerValue = isRadioORCheckbox(answerType)
            ? [
                  {
                      optionName: 'ตัวเลือก 1',
                  },
              ]
            : [];
        return {
            id: uuidv4(),
            questionName: '',
            questionDescription: '',
            isRequire: true,
            isDescription: false,
            isSkip: false,
            answerType,
            answerValue,
            storyId: null,
            isShowOther: false,
        };
    };

    const initGroup = {
        id: uuidv4(),
        partName: '',
        partDescription: '',
        skipToPart: null,
        groupQuestion: [],
    };

    const setFieldValue = useCallback(
        (value) => {
            form.setFieldValue('part', value);
        },
        [form]
    );

    const onDragEnd = (result) => {
        const { source, destination } = result;
        // dropped outside the list
        if (!destination) {
            return;
        }
        const sInd = +source.droppableId;
        const dInd = +destination.droppableId;

        if (sInd === dInd) {
            const items = reorder(
                part[sInd].groupQuestion,
                source.index,
                destination.index
            );
            const newState = [...part];
            newState[sInd].groupQuestion = items;
            setFieldValue(newState);
        } else {
            const result = move(
                part[sInd].groupQuestion,
                part[dInd].groupQuestion,
                source,
                destination
            );
            const newState = [...part];
            newState[sInd].groupQuestion = result[sInd];
            newState[dInd].groupQuestion = result[dInd];
            setFieldValue(newState);
        }
    };

    const forms = [
        {
            label: 'ชื่อแบบฟอร์ม',
            name: 'formName',
            rules: [
                {
                    validator: (_, value) => {
                        if (!textOnly(value)?.trim()) {
                            return Promise.reject(
                                new Error('กรุณากรอกชื่อแบบฟอร์ม')
                            );
                        }
                        return Promise.resolve();
                    },
                },
            ],
            editor: {
                title: true,
            },
        },
        {
            label: 'คำอธิบายแบบฟอร์ม',
            name: 'formDescription',
            initialValue: '',
            rules: [],
        },
    ];

    const generateFormPart = useCallback((index) => {
        return [
            {
                label: 'ชื่อส่วน',
                name: [index, 'partName'],
                initialValue: '',
                rules: [
                    {
                        validator: (_, value) => {
                            if (!textOnly(value)?.trim()) {
                                return Promise.reject(
                                    new Error('กรุณากรอกชื่อส่วน')
                                );
                            }
                            return Promise.resolve();
                        },
                    },
                ],
            },
            {
                label: 'คำอธิบายส่วน',
                name: [index, 'partDescription'],
                initialValue: '',
                rules: [],
                editor: {
                    description: true,
                },
            },
        ];
    }, []);

    useEffect(() => {
        return () => {
            Events.scrollEvent.remove('begin');
            Events.scrollEvent.remove('end');
        };
    }, []);

    const scrollToWithContainer = useCallback((id) => {
        const goToContainer = new Promise((resolve) => {
            Events.scrollEvent.register('end', () => {
                resolve();
                Events.scrollEvent.remove('end');
            });

            scroller.scrollTo('form-breach', {
                duration: 300,
                delay: 0,
                smooth: 'easeInOutQuart',
            });
        });

        goToContainer.then(() =>
            scroller.scrollTo(id, {
                duration: 300,
                delay: 0,
                smooth: 'easeInOutQuart',
                containerId: 'form-breach',
                offset: -100,
            })
        );
    }, []);

    const scrollAndSetSelectItem = useCallback(
        (selectItem) => {
            setSpinning(true);
            setTimeout(() => {
                setSelectItem(selectItem);
                scrollToWithContainer(selectItem.id);
            }, 300);
            setTimeout(() => {
                setSpinning(false);
            }, 500);
        },
        [scrollToWithContainer]
    );

    const handleAddItem = useCallback(
        (key, qtype) => {
            if (!selectItem) return;

            const newState = [...form.getFieldValue('part')];

            const addItemToGroup = (groupIndex, item) => {
                newState[groupIndex].groupQuestion.unshift(item);
                setFieldValue(newState);
                scrollAndSetSelectItem({
                    id: item.id,
                    group: newState[groupIndex].id,
                    type: 'item',
                });
            };

            const addItemAfterItem = (groupIndex, itemIndex, item) => {
                newState[groupIndex].groupQuestion.splice(
                    itemIndex + 1,
                    0,
                    item
                );
                setFieldValue(newState);
                scrollAndSetSelectItem({
                    id: item.id,
                    group: newState[groupIndex].id,
                    type: 'item',
                });
            };

            const addGroup = (groupIndex) => {
                newState.splice(groupIndex + 1, 0, initGroup);
                setFieldValue(newState);
                scrollAndSetSelectItem({
                    id: newState[groupIndex + 1].id,
                    type: 'group',
                });
            };

            switch (key) {
                case 'group':
                    if (selectItem.id === 'main') {
                        setFieldValue([initGroup, ...part]);
                        scrollAndSetSelectItem({
                            id: initGroup.id,
                            type: 'group',
                        });
                    } else if (selectItem.type === 'group') {
                        const groupIndex = newState.findIndex(
                            (group) => group.id === selectItem.id
                        );
                        addGroup(groupIndex);
                    } else if (selectItem.type === 'item') {
                        const groupIndex = newState.findIndex(
                            (group) => group.id === selectItem.group
                        );
                        addGroup(groupIndex);
                    }
                    break;
                case 'item':
                    if (!qtype) return;
                    {
                        const newItem = initItem(qtype);
                        if (selectItem.type === 'group') {
                            const groupIndex = newState.findIndex(
                                (group) => group.id === selectItem.id
                            );
                            addItemToGroup(groupIndex, newItem);
                        } else if (selectItem.type === 'item') {
                            const groupIndex = newState.findIndex(
                                (group) => group.id === selectItem.group
                            );
                            const itemIndex = newState[
                                groupIndex
                            ].groupQuestion.findIndex(
                                (item) => item.id === selectItem.id
                            );
                            addItemAfterItem(groupIndex, itemIndex, newItem);
                        }
                    }
                    break;
                default:
                    break;
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [selectItem, part, initGroup]
    );

    const dulicateGroup = (index) => {
        const newState = [...part];
        const group = {
            ...newState[index],
            id: uuidv4(),
            groupQuestion: newState[index].groupQuestion.map((item) => ({
                ...item,
                id: uuidv4(),
            })),
        };
        newState.splice(index + 1, 0, group);

        scrollAndSetSelectItem({
            id: group.id,
            type: 'group',
        });
        setFieldValue(newState);
    };

    const duplicateItem = (groupIndex, index) => {
        const newState = [...part];
        const item = { ...newState[groupIndex].groupQuestion[index] };
        item.id = uuidv4();
        newState[groupIndex].groupQuestion.splice(index + 1, 0, item);

        scrollAndSetSelectItem({
            id: item.id,
            group: newState[groupIndex].id,
            type: 'item',
        });
        setFieldValue(newState);
    };

    useEffect(() => {
        if (activeQuestionRef?.current) {
            setPanelTopValue(activeQuestionRef.current.offsetTop);
        }
    }, [activeQuestionRef]);

    const onActiveQuestion = (ref) => {
        setActiveQuestionRef(ref);
    };

    const removePart = (remove, index) => {
        const parts = form.getFieldValue('part');

        if (!parts || isNaN(index)) return;

        const previousGroup = parts[index - 1]?.groupQuestion;
        const currentGroupId = parts[index]?.id;
        const previousGroupId = parts[index - 1]?.id;

        const delayedRemoveAndSelect = (item) => {
            setTimeout(() => {
                remove(index);
                scrollAndSetSelectItem(item);
                form.validateFields();
            }, 300);
        };

        if (previousGroup?.length > 0) {
            delayedRemoveAndSelect({
                id: previousGroup[previousGroup.length - 1].id,
                group: currentGroupId,
                type: 'item',
            });
        } else if (previousGroupId) {
            delayedRemoveAndSelect({
                id: previousGroupId,
                type: 'group',
            });
        } else {
            delayedRemoveAndSelect({
                id: 'main',
                type: 'main',
            });
        }
    };

    return (
        <Spin spinning={spinning} className="">
            <div className="space-y-4 relative max-w-2xl">
                <MenuBar
                    topValue={panelTopValue}
                    handleAddItem={handleAddItem}
                    selectItem={selectItem}
                />
                <Card
                    onClick={() => {
                        setSelectItem({
                            id: 'main',
                            type: 'main',
                        });
                    }}
                    selectId={selectItem?.id}
                    id="main"
                    onActiveQuestion={onActiveQuestion}
                    isActived={selectItem?.type === 'main'}
                >
                    <Forms forms={forms} />
                </Card>
                <Form.List name="part">
                    {(_, { remove }) => (
                        <DragDropContext onDragEnd={onDragEnd}>
                            {form.getFieldValue('part')?.map((_part, ind) => (
                                <Droppable
                                    key={ind}
                                    droppableId={ind.toString()}
                                >
                                    {(provided) => (
                                        <div
                                            className="space-y-4"
                                            ref={provided.innerRef}
                                            {...provided.droppableProps}
                                        >
                                            <Card
                                                onClick={() => {
                                                    setSelectItem({
                                                        id: _part.id,
                                                        type: 'group',
                                                    });
                                                }}
                                                selectId={selectItem?.id}
                                                id={_part.id}
                                                onActiveQuestion={
                                                    onActiveQuestion
                                                }
                                                isActived={
                                                    selectItem?.type ===
                                                        'group' &&
                                                    selectItem?.id === _part.id
                                                }
                                            >
                                                <FormsPart
                                                    openModal={() =>
                                                        setOpenModal(true)
                                                    }
                                                    remove={() => {
                                                        removePart(remove, ind);
                                                    }}
                                                    part={ind + 1}
                                                    partLength={part.length}
                                                    forms={generateFormPart(
                                                        ind
                                                    )}
                                                    dulicateGroup={() =>
                                                        dulicateGroup(ind)
                                                    }
                                                />
                                            </Card>
                                            {_part?.groupQuestion
                                                ?.filter(
                                                    (item) => item.answerType
                                                )
                                                ?.map((item, index) => (
                                                    <Draggable
                                                        key={item.id}
                                                        draggableId={item.id}
                                                        index={index}
                                                    >
                                                        {(
                                                            provided,
                                                            snapshot
                                                        ) => (
                                                            <div
                                                                ref={
                                                                    provided.innerRef
                                                                }
                                                                {...provided.draggableProps}
                                                                style={getItemStyle(
                                                                    snapshot.isDragging,
                                                                    provided
                                                                        .draggableProps
                                                                        .style
                                                                )}
                                                            >
                                                                <Card
                                                                    onClick={() =>
                                                                        setSelectItem(
                                                                            {
                                                                                id: item.id,
                                                                                group: _part.id,
                                                                                type: 'item',
                                                                            }
                                                                        )
                                                                    }
                                                                    id={item.id}
                                                                    selectId={
                                                                        selectItem?.id
                                                                    }
                                                                    onActiveQuestion={
                                                                        onActiveQuestion
                                                                    }
                                                                    isActived={
                                                                        selectItem?.type ===
                                                                            'item' &&
                                                                        selectItem?.id ===
                                                                            item.id
                                                                    }
                                                                >
                                                                    <FormsSub
                                                                        duplicateItem={() =>
                                                                            duplicateItem(
                                                                                ind,
                                                                                index
                                                                            )
                                                                        }
                                                                        dragHandleProps={
                                                                            provided.dragHandleProps
                                                                        }
                                                                        setSelectItem={
                                                                            scrollAndSetSelectItem
                                                                        }
                                                                        items={{
                                                                            item,
                                                                            index,
                                                                            groupIndex:
                                                                                ind,
                                                                            options:
                                                                                template,
                                                                        }}
                                                                    />
                                                                </Card>
                                                            </div>
                                                        )}
                                                    </Draggable>
                                                ))}
                                            {provided.placeholder}
                                            <Skiptopart index={ind} />
                                        </div>
                                    )}
                                </Droppable>
                            ))}
                        </DragDropContext>
                    )}
                </Form.List>
                <MovePart
                    scrollAndSetSelectItem={() =>
                        scrollAndSetSelectItem(selectItem)
                    }
                    form={form}
                    open={openModal}
                    handelClose={() => setOpenModal(false)}
                />
            </div>
        </Spin>
    );
}

Move.propTypes = {
    template: PropTypes.array.isRequired,
};
